// ============================================================================
// 📦 VERSION: 2025.12.12.1345
// ============================================================================
// TRAK Quiz Game - API Helper
// Base URL: https://api.traktag.com/api
// ============================================================================

const API_URL = 'https://api.traktag.com/api';

// ============================================
// TOKEN MANAGEMENT
// ============================================
export const setToken = (token) => localStorage.setItem('trak_token', token);
export const getToken = () => localStorage.getItem('trak_token');
export const clearToken = () => localStorage.removeItem('trak_token');

// Auth headers helper
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`
});

// ============================================
// AUTH ENDPOINTS
// ============================================

// Register new player (with optional phone data)
export const register = async (email, password, name, phoneData = null) => {
  const payload = { 
    email, 
    password, 
    name 
  };
  
  // Add phone data if provided
  if (phoneData && phoneData.phoneNumber) {
    payload.phone_country_code = phoneData.countryCode;   // e.g., "GB"
    payload.phone_dial_code = phoneData.dialCode;         // e.g., "+44"
    payload.phone_number = phoneData.phoneNumber;         // e.g., "7911123456"
  }
  
  const res = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await res.json();
  if (data.token) setToken(data.token);
  return data;
};

// Login player
export const login = async (email, password) => {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  const data = await res.json();
  if (data.token) setToken(data.token);
  return data;
};

// Get current player profile
export const getProfile = async () => {
  const res = await fetch(`${API_URL}/auth/me`, {
    headers: authHeaders()
  });
  return res.json();
};

// Update player profile (including phone)
export const updateProfile = async (profileData) => {
  const res = await fetch(`${API_URL}/auth/profile`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(profileData)
  });
  return res.json();
};

// Logout (clear local token)
export const logout = () => {
  clearToken();
};

// ============================================
// GAMES ENDPOINTS
// ============================================

// Get all active games
export const getGames = async () => {
  const res = await fetch(`${API_URL}/games`);
  return res.json();
};

// Get single game
export const getGame = async (gameId) => {
  const res = await fetch(`${API_URL}/games/${gameId}`);
  return res.json();
};

// Create game (admin)
export const createGame = async (gameData) => {
  const res = await fetch(`${API_URL}/games`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(gameData)
  });
  return res.json();
};

// Update game (admin)
export const updateGame = async (gameId, gameData) => {
  const res = await fetch(`${API_URL}/games/${gameId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(gameData)
  });
  return res.json();
};

// ============================================
// QUESTIONS ENDPOINTS
// ============================================

// Get questions for a game
export const getQuestions = async (gameId) => {
  const res = await fetch(`${API_URL}/games/${gameId}/questions`);
  return res.json();
};

// Add single question (admin)
export const addQuestion = async (gameId, questionData) => {
  const res = await fetch(`${API_URL}/games/${gameId}/questions`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(questionData)
  });
  return res.json();
};

// Bulk upload questions (admin)
export const bulkUploadQuestions = async (gameId, questions) => {
  const res = await fetch(`${API_URL}/games/${gameId}/questions/bulk`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ questions })
  });
  return res.json();
};

// ============================================
// QUESTION TRACKING
// ============================================

// Record question attempt (for difficulty calculation)
export const recordQuestionAttempt = async (questionId, correct) => {
  const res = await fetch(`${API_URL}/questions/${questionId}/attempt`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ correct })
  });
  return res.json();
};

// ============================================
// PLAYER STATS ENDPOINTS
// ============================================

// Update player stats after game session
export const updateStats = async (stats) => {
  const res = await fetch(`${API_URL}/players/stats`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(stats)
  });
  return res.json();
};

// ============================================
// HELPER: Check if logged in
// ============================================
export const isLoggedIn = () => {
  return !!getToken();
};
